import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider } from 'antd'
import { TenantProvider } from './app/contexts'
import App from './App'
import './styles/index.css'
import './styles/globals.scss'
import './styles/icons.scss'

// React Query client with caching strategy
// Matches Next.js configuration for consistent behavior
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default staleTime: 60 seconds (1 minute)
      // Individual queries can override this (e.g., conversation list uses Infinity)
      staleTime: 60 * 1000,
      // Default gcTime (formerly cacheTime): 5 minutes
      // Cached data is kept in memory for 5 minutes after becoming unused
      gcTime: 5 * 60 * 1000,
      // Disable automatic refetching on window focus
      refetchOnWindowFocus: false,
      // Disable automatic refetching on reconnect
      refetchOnReconnect: false,
      // Disable retries by default (individual queries can override)
      retry: 0,
      // Don't throw errors by default
      throwOnError: false,
    },
  },
})

// QA Genie Green Theme Configuration
const theme = {
  token: {
    // Primary Colors (Green Theme)
    colorPrimary: '#16A34A',        // Green 600
    colorSuccess: '#10B981',        // Green 500
    colorWarning: '#F59E0B',        // Amber 500
    colorError: '#EF4444',          // Red 500
    colorInfo: '#3B82F6',           // Blue 500
    
    // Border & Radius
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    
    // Font
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
    fontSize: 14,
    
    // Spacing
    padding: 16,
    margin: 16,
    
    // Colors
    colorBgContainer: '#FFFFFF',
    colorBgLayout: '#F9FAFB',
    colorBorder: '#E5E7EB',
    colorText: '#111827',
    colorTextSecondary: '#6B7280',
  },
  components: {
    Button: {
      primaryColor: '#FFFFFF',
      colorPrimaryHover: '#15803D',
      colorPrimaryActive: '#166534',
    },
    Input: {
      colorBorder: '#E5E7EB',
      colorPrimaryHover: '#16A34A',
    },
    Select: {
      colorBorder: '#E5E7EB',
      colorPrimaryHover: '#16A34A',
    },
    Table: {
      colorBorderSecondary: '#E5E7EB',
      colorFillAlter: '#F9FAFB',
    },
    Menu: {
      colorItemBgSelected: '#DCFCE7',
      colorItemTextSelected: '#16A34A',
    },
    Tabs: {
      colorPrimaryHover: '#15803D',
    },
  },
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <TenantProvider>
          <ConfigProvider theme={theme}>
            <App />
          </ConfigProvider>
        </TenantProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
)
